import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, Rocket, EyeOff, ImagePlus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import VideoUploadForm from "@/components/creator/VideoUploadForm";
import VideoManageRow from "@/components/creator/VideoManageRow";
import ManualNotesEditor from "@/components/video-notes/ManualNotesEditor";
import { CourseThumbnail } from "@/components/CourseThumbnail";
import MediaLibrarySection from "@/components/creator/MediaLibrarySection";
import BossBattleSection from "@/components/creator/BossBattleSection";

export default function CreatorCourseEditorPage() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingInfo, setSavingInfo] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", price: "" });
  const [uploadingThumb, setUploadingThumb] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/courses/${courseId}`);
    setCourse(data);
    setForm({ title: data.title, description: data.description, price: data.price });
    return data;
  }, [courseId]);

  useEffect(() => {
    setLoading(true);
    load().catch(() => toast.error("Course not found")).finally(() => setLoading(false));
  }, [load]);

  // Poll while any video is still processing AI notes
  useEffect(() => {
    if (!course) return;
    const anyProcessing = course.videos.some((v) => v.transcript_status === "processing");
    if (!anyProcessing) return;
    const t = setTimeout(() => load(), 5000);
    return () => clearTimeout(t);
  }, [course, load]);

  const saveInfo = async () => {
    setSavingInfo(true);
    try {
      await api.patch(`/courses/${courseId}`, { ...form, price: Number(form.price) || 0 });
      toast.success("Course details saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingInfo(false);
    }
  };

  const togglePublish = async () => {
    setPublishing(true);
    try {
      const nextStatus = course.status === "published" ? "draft" : "published";
      await api.patch(`/courses/${courseId}`, { status: nextStatus });
      toast.success(nextStatus === "published" ? "Course published!" : "Course unpublished");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPublishing(false);
    }
  };

  const deleteVideo = async (videoId) => {
    try {
      await api.delete(`/courses/${courseId}/videos/${videoId}`);
      toast.success("Video removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploadingThumb(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch(`/courses/${courseId}`, { thumbnail_url: data.url });
      toast.success("Thumbnail updated!");
      load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to upload thumbnail");
    } finally {
      setUploadingThumb(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="creator-course-editor-loading">
        <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!course) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="creator-course-editor-page">
      <Link to="/creator/studio" data-testid="course-editor-back-link" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Creator Studio
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white">{course.title}</h1>
          <span className={`inline-block mt-1.5 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${course.status === "published" ? "bg-emerald-400/10 text-emerald-300" : "bg-slate-400/10 text-slate-400"}`} data-testid="course-editor-status-badge">{course.status}</span>
        </div>
        <button onClick={togglePublish} disabled={publishing} data-testid="course-editor-publish-toggle-btn"
          className={`flex items-center gap-2 font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors disabled:opacity-60 ${course.status === "published" ? "bg-white/10 hover:bg-white/15 text-white" : "bg-emerald-500 hover:bg-emerald-400 text-white"}`}>
          {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : course.status === "published" ? <EyeOff className="w-4 h-4" /> : <Rocket className="w-4 h-4" />}
          {course.status === "published" ? "Unpublish" : "Publish Course"}
        </button>
      </div>

      <div className="glass-panel rounded-2xl p-5 mb-6">
        <h3 className="font-heading text-sm font-semibold text-white mb-3">Course Details</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-400 mb-1.5 block">Thumbnail</label>
            <div className="flex items-center gap-4">
              <CourseThumbnail url={course.thumbnail_url} size="w-40 aspect-video" className="shrink-0" testId="course-editor-thumbnail-preview" />
              <label className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-slate-300 cursor-pointer transition-colors">
                {uploadingThumb ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                {uploadingThumb ? "Uploading..." : "Upload Thumbnail"}
                <input type="file" accept="image/*" onChange={handleThumbnailChange} disabled={uploadingThumb} data-testid="course-editor-thumbnail-input" className="hidden" />
              </label>
            </div>
          </div>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="course-editor-title-input"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-primary/50" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="course-editor-description-input"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-primary/50 resize-none" />
          <div>
            <label className="text-xs font-semibold text-slate-400 mb-1.5 block">Price (₹, 0 for free)</label>
            <input type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="course-editor-price-input"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-primary/50" />
          </div>
          <button onClick={saveInfo} disabled={savingInfo} data-testid="course-editor-save-info-btn"
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors">
            {savingInfo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Details
          </button>
        </div>
      </div>

      <div className="mb-6">
        <h3 className="font-heading text-sm font-semibold text-white mb-3">Lectures ({course.videos.length})</h3>
        <div className="space-y-2 mb-4">
          {course.videos.map((v) => <VideoManageRow key={v.id} video={v} onDelete={deleteVideo} />)}
          {course.videos.length === 0 && <p className="text-xs text-slate-500">No lectures yet — upload your first video below.</p>}
        </div>
        <VideoUploadForm courseId={courseId} onUploaded={load} />
      </div>

      <ManualNotesEditor endpoint={`/courses/${courseId}/notes`} initialNotes={course.manual_notes} onSaved={(notes) => setCourse((prev) => ({ ...prev, manual_notes: notes }))} />

      <MediaLibrarySection courseId={courseId} resources={course.resources || []} onChanged={load} />

      <BossBattleSection courseId={courseId} battles={course.boss_battles || []} onChanged={load} />
    </div>
  );
}
